import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { confidentialAPI } from '../services/api';
import {
  DEFAULT_GUIDE_LANG,
  NATIONALITY_TO_GUIDE_LANGS,
  isLanguageReady,
} from '../config/userGuideLanguages';

const PREFERENCE_KEY_PREFIX = 'userGuideLocale';

const readStoredValue = (key) => {
  if (!key) return null;
  try {
    return localStorage.getItem(key);
  } catch (_) {
    return null;
  }
};

const writeStoredValue = (key, value) => {
  if (!key) return;
  try {
    localStorage.setItem(key, value);
  } catch (_) {
    // Storage can be unavailable in private/restricted browser contexts.
  }
};

/**
 * Resolve and persist the User Guide language for the signed-in employee.
 *
 * The configured nationality match is applied automatically on first use.
 * Workers can still override it from the language selector.
 *
 * @param {object|null} user Authenticated StaffUser payload.
 * @returns {object} Selected locale and language-selection control.
 */
export const useUserGuideLanguage = (user) => {
  const staffKey = user?.staff_id || user?.id || null;
  const preferenceKey = staffKey ? `${PREFERENCE_KEY_PREFIX}:${staffKey}` : null;
  const isWorker = user?.employment_classification === 'worker';

  const [locale, setLocale] = useState(DEFAULT_GUIDE_LANG);
  const [hasExplicitPreference, setHasExplicitPreference] = useState(false);

  useEffect(() => {
    const storedLocale = readStoredValue(preferenceKey);
    const hasValidStoredLocale = Boolean(storedLocale && isLanguageReady(storedLocale));

    setLocale(hasValidStoredLocale ? storedLocale : DEFAULT_GUIDE_LANG);
    setHasExplicitPreference(hasValidStoredLocale);
  }, [preferenceKey]);

  const { data: myProfile } = useQuery({
    queryKey: ['userGuide', 'languageProfile', staffKey],
    queryFn: async () => {
      const response = await confidentialAPI.getMyHRProfile();
      return response.data;
    },
    enabled: Boolean(isWorker && staffKey && !hasExplicitPreference),
    staleTime: 30 * 60 * 1000,
  });

  const suggestedLocale = useMemo(() => {
    const nationality = (myProfile?.hrprofile_nationality || '').trim().toLowerCase();
    const codes = NATIONALITY_TO_GUIDE_LANGS[nationality] || [];
    return codes.find((code) => isLanguageReady(code)) || null;
  }, [myProfile]);

  const persistLocale = useCallback(
    (code) => {
      if (!isLanguageReady(code)) return;
      setLocale(code);
      setHasExplicitPreference(true);
      writeStoredValue(preferenceKey, code);
    },
    [preferenceKey]
  );

  useEffect(() => {
    if (!hasExplicitPreference && suggestedLocale) {
      persistLocale(suggestedLocale);
    }
  }, [hasExplicitPreference, persistLocale, suggestedLocale]);

  return {
    locale,
    changeLocale: persistLocale,
  };
};
